import { X } from 'lucide-react';
import { MessageCircle, BookOpen, GraduationCap, Users, Wrench, MessageSquare, Star, NotebookPen } from 'lucide-react';
import Button from '../../../components/Button';
import type { QuestTemplateFirestore, QuestType } from '../services/questTypes';

interface SearchResult {
    questTemplate: QuestTemplateFirestore;
    journeyDayId: string;
    matchedText: string;
}

interface QuestTemplateSearchResultsProps {
    results: SearchResult[];
    searchQuery: string;
    onSelect: (journeyDayId: string) => void;
    onClose: () => void;
}

// Helper function to get quest type icons
const getQuestTypeIcon = (type: QuestType): React.ReactNode => {
    const iconClass = "w-5 h-5";
    const icons: Record<QuestType, React.ReactNode> = {
        'chat': <MessageCircle className={iconClass} />,
        'check-in': <NotebookPen className={iconClass} />,
        'story': <BookOpen className={iconClass} />,
        'lesson': <GraduationCap className={iconClass} />,
        'community-question': <Users className={iconClass} />,
        'tool': <Wrench className={iconClass} />,
        'feedback': <MessageSquare className={iconClass} />,
        'rate': <Star className={iconClass} />
    };
    return icons[type];
};

// Helper function to get quest type labels
const getQuestTypeLabel = (type: QuestType): string => {
    const labels: Record<QuestType, string> = {
        'chat': 'Chat',
        'check-in': 'Check-in',
        'story': 'Story',
        'lesson': 'Lesson',
        'community-question': 'Community Question',
        'tool': 'Tool',
        'feedback': 'Feedback',
        'rate': 'Rate'
    };
    return labels[type];
};

// Helper function to get quest type colors
const getQuestTypeColor = (type: QuestType): string => {
    const colors: Record<QuestType, string> = {
        'chat': 'bg-blue-500',
        'check-in': 'bg-green-500',
        'story': 'bg-purple-500',
        'lesson': 'bg-indigo-500',
        'community-question': 'bg-pink-500',
        'tool': 'bg-orange-500',
        'feedback': 'bg-teal-500',
        'rate': 'bg-yellow-500'
    };
    return colors[type];
};

// Helper function to highlight search term in text
const highlightText = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) {
        return text;
    }

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
        <>
            {parts.map((part, index) => 
                part.toLowerCase() === query.toLowerCase() ? (
                    <mark key={index} className="bg-yellow-200 font-semibold">{part}</mark>
                ) : (
                    <span key={index}>{part}</span>
                )
            )}
        </>
    );
};

// Helper function to truncate text with ellipsis
const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }
    return text.substring(0, maxLength) + '...';
};

export const QuestTemplateSearchResults = ({ results, searchQuery, onSelect, onClose }: QuestTemplateSearchResultsProps) => {
    const handleResultClick = (journeyDayId: string) => {
        onSelect(journeyDayId);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600/10 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto p-4 w-full max-w-3xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center gap-2 mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                        Search Results ({results.length})
                    </h3>
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                {results.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No quest templates found matching "{searchQuery}"
                    </div>
                ) : (
                    <div className="space-y-3">
                        {results.map((result, index) => {
                            const { questTemplate, journeyDayId, matchedText } = result;
                            const dayNumber = journeyDayId.replace('day_', '');
                            
                            return (
                                <button
                                    key={`${questTemplate.id}-${index}`}
                                    onClick={() => handleResultClick(journeyDayId)}
                                    className="w-full text-left border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${getQuestTypeColor(questTemplate.type)}`}>
                                                {getQuestTypeIcon(questTemplate.type)}
                                                {getQuestTypeLabel(questTemplate.type)}
                                            </span>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    Day {dayNumber}
                                                </span>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    Order #{questTemplate.order}
                                                </span>
                                                {questTemplate.isPublished ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Published
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Unpublished
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {questTemplate.pillText && (
                                                <div className="text-sm font-semibold text-gray-900 mb-1">
                                                    {questTemplate.pillText}
                                                </div>
                                            )}
                                            
                                            <div className="text-sm text-gray-600 line-clamp-2">
                                                {highlightText(truncateText(matchedText, 150), searchQuery)}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="flex justify-end mt-4">
                    <Button
                        onClick={onClose}
                        variant="secondary"
                    >
                        Close (Esc)
                    </Button>
                </div>
            </div>
        </div>
    );
};
